import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui/button'
import { getDocument, typedDoc } from '@/lib/firebase/firestore'
import {
  getActiveHomeHeroBanners,
  normalizeHomeHeroConfig,
  type HomeHeroConfig,
} from '@/lib/homeHero'
import type { HomeHeroBannerItem } from '@/types'

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

interface HomeHeroBannerViewProps {
  hero: HomeHeroBannerItem
  /** When true, CTA renders as an inert button for the admin preview. */
  preview?: boolean
  /** The public carousel owns slide transitions, so it can skip the initial height reveal. */
  animateHeight?: boolean
  carouselControlsInset?: boolean
  slideLabel?: string
}

/** Pure presentational banner used by both the live home page and the admin preview. */
export function HomeHeroBannerView({
  hero,
  preview = false,
  animateHeight = true,
  carouselControlsInset = false,
  slideLabel,
}: HomeHeroBannerViewProps) {
  const color = hero.textColor || '#ffffff'
  const [assetsReady, setAssetsReady] = useState(preview)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)

  useEffect(() => {
    if (preview) {
      setAssetsReady(true)
      return
    }
    const urls = [hero.backgroundImageUrl, hero.logoImageUrl].filter(Boolean)
    if (urls.length === 0) {
      setAssetsReady(true)
      return
    }
    let cancelled = false
    let remaining = urls.length
    const done = () => {
      if (cancelled) return
      remaining -= 1
      if (remaining <= 0) setAssetsReady(true)
    }
    urls.forEach((src) => {
      const image = new Image()
      image.onload = done
      image.onerror = done
      image.src = src
    })
    return () => {
      cancelled = true
    }
  }, [hero.backgroundImageUrl, hero.logoImageUrl, preview])

  useLayoutEffect(() => {
    if (!animateHeight || !assetsReady || !innerRef.current) return
    const element = innerRef.current
    const measure = () => setMeasuredHeight(element.scrollHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [animateHeight, assetsReady])

  const backgroundStyle = hero.backgroundImageUrl
    ? {
        backgroundImage: `url(${hero.backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: `${hero.backgroundFocusX * 100}% ${hero.backgroundFocusY * 100}%`,
      }
    : undefined

  const ctaButton =
    hero.ctaLabel && hero.ctaUrl ? (
      preview ? (
        <Button size="lg" type="button" onClick={(event) => event.preventDefault()}>
          {hero.ctaLabel}
        </Button>
      ) : isExternal(hero.ctaUrl) || hero.ctaOpenInNewTab ? (
        <a
          href={hero.ctaUrl}
          target={hero.ctaOpenInNewTab ? '_blank' : undefined}
          rel={hero.ctaOpenInNewTab ? 'noopener noreferrer' : undefined}
        >
          <Button size="lg">{hero.ctaLabel}</Button>
        </a>
      ) : (
        <Link to={hero.ctaUrl}>
          <Button size="lg">{hero.ctaLabel}</Button>
        </Link>
      )
    ) : null

  const innerContent = (
    <div ref={innerRef}>
      {hero.backgroundImageUrl && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(0,0,0,${hero.overlayOpacity})` }}
        />
      )}
      <div
        className={`relative container mx-auto px-4 py-6 md:py-8 ${
          carouselControlsInset ? 'pb-20 md:pb-20' : ''
        }`}
      >
        <div className="flex flex-col items-center gap-3 text-center" style={{ color }}>
          {hero.logoImageUrl && (
            <img
              src={hero.logoImageUrl}
              alt=""
              className="max-h-48 w-auto object-contain md:max-h-64"
            />
          )}
          {hero.bodyMarkdown.trim() && (
            <div
              className="prose w-full max-w-2xl
                [&_*]:!text-[color:inherit]
                [&_h1]:!mt-0 [&_h2]:!mt-0 [&_h3]:!mt-1
                [&_h1]:!mb-1 [&_h2]:!mb-1 [&_h3]:!mb-1
                [&_p]:!my-1 [&_ul]:!my-1 [&_ol]:!my-1
                [&_a]:underline [&_a]:!text-[color:inherit]"
              style={{ color }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{hero.bodyMarkdown}</ReactMarkdown>
            </div>
          )}
          {ctaButton}
        </div>
      </div>
    </div>
  )

  if (!assetsReady) return null

  if (!animateHeight) {
    return (
      <section
        className="relative overflow-hidden bg-muted"
        style={backgroundStyle}
        aria-label={slideLabel}
        aria-roledescription={slideLabel ? 'slide' : undefined}
      >
        {innerContent}
      </section>
    )
  }

  return (
    <AnimatePresence initial={true}>
      <motion.section
        key="home-hero-banner"
        initial={{ height: 0 }}
        animate={{ height: measuredHeight ?? 0 }}
        exit={{ height: 0 }}
        transition={{ duration: 0.5, ease: 'linear' }}
        className="relative overflow-hidden bg-muted"
        style={backgroundStyle}
      >
        {innerContent}
      </motion.section>
    </AnimatePresence>
  )
}

interface HomeHeroCarouselViewProps {
  config: HomeHeroConfig
  preview?: boolean
}

export function HomeHeroCarouselView({ config, preview = false }: HomeHeroCarouselViewProps) {
  const activeBanners = useMemo(() => getActiveHomeHeroBanners(config), [config])
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [pointerPaused, setPointerPaused] = useState(false)
  const [focusPaused, setFocusPaused] = useState(false)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    setActiveIndex((current) => (current < activeBanners.length ? current : 0))
  }, [activeBanners.length])

  useEffect(() => {
    if (activeBanners.length < 2 || paused || pointerPaused || focusPaused || reducedMotion) {
      return
    }
    const timer = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % activeBanners.length)
    }, config.rotationIntervalSeconds * 1_000)
    return () => window.clearTimeout(timer)
  }, [
    activeBanners.length,
    activeIndex,
    config.rotationIntervalSeconds,
    focusPaused,
    paused,
    pointerPaused,
    reducedMotion,
  ])

  if (activeBanners.length === 0) return null
  const currentBanner = activeBanners[activeIndex] ?? activeBanners[0]
  if (!currentBanner) return null

  if (activeBanners.length === 1) {
    return <HomeHeroBannerView hero={currentBanner} preview={preview} />
  }

  function showBanner(index: number) {
    const normalizedIndex = (index + activeBanners.length) % activeBanners.length
    if (normalizedIndex === activeIndex) return
    setActiveIndex(normalizedIndex)
  }

  return (
    <div
      className="relative overflow-hidden bg-muted"
      role="region"
      aria-label="Home page announcements"
      aria-roledescription="carousel"
      onMouseEnter={() => setPointerPaused(true)}
      onMouseLeave={() => setPointerPaused(false)}
      onFocusCapture={() => setFocusPaused(true)}
      onBlurCapture={(event) => {
        const nextFocus = event.relatedTarget
        if (!(nextFocus instanceof Node) || !event.currentTarget.contains(nextFocus)) {
          setFocusPaused(false)
        }
      }}
    >
      <div className="overflow-hidden" aria-live={paused ? 'polite' : 'off'}>
        <div
          className={`flex items-stretch ${
            reducedMotion ? '' : 'transition-transform duration-500 ease-in-out'
          }`}
          style={{ transform: `translate3d(-${activeIndex * 100}%, 0, 0)` }}
        >
          {activeBanners.map((banner, index) => (
            <div
              key={banner.id}
              className="w-full shrink-0"
              aria-hidden={index !== activeIndex}
              inert={index !== activeIndex}
            >
              <HomeHeroBannerView
                hero={banner}
                preview={preview}
                animateHeight={false}
                carouselControlsInset
                slideLabel={`Banner ${index + 1} of ${activeBanners.length}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-2 z-10 flex items-center justify-center gap-1 px-2">
        <div className="flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-full bg-black/60 px-1 text-white shadow-lg backdrop-blur-sm">
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            onClick={() => showBanner(activeIndex - 1)}
            aria-label="Show previous banner"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <div
            className="flex min-w-0 items-center overflow-x-auto"
            role="group"
            aria-label="Choose a banner"
          >
            {activeBanners.map((banner, index) => (
              <button
                key={banner.id}
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                onClick={() => showBanner(index)}
                aria-label={`Show banner ${index + 1}`}
                aria-current={index === activeIndex ? 'true' : undefined}
              >
                <span
                  className={`block rounded-full transition-all ${
                    index === activeIndex ? 'h-2.5 w-6 bg-white' : 'h-2.5 w-2.5 bg-white/55'
                  }`}
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            onClick={() => showBanner(activeIndex + 1)}
            aria-label="Show next banner"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            onClick={() => setPaused((current) => !current)}
            aria-label={
              paused ? 'Resume automatic banner rotation' : 'Pause automatic banner rotation'
            }
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  )
}

export function HomeHeroBanner() {
  const [config, setConfig] = useState<HomeHeroConfig | null>(null)

  useEffect(() => {
    getDocument(typedDoc<Record<string, unknown>>('siteConfig', 'homeHero'))
      .then((stored) => setConfig(stored ? normalizeHomeHeroConfig(stored) : null))
      .catch(console.error)
  }, [])

  if (!config) return null
  return <HomeHeroCarouselView config={config} />
}
